import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';

function FAQItem({ question, answer }: { question: string; answer: string }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="border border-background-200/70 rounded-xl bg-background-50 overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-5 text-left hover:bg-background-100 transition-colors"
      >
        <span className="font-medium text-foreground-950 text-sm md:text-base pr-4">{question}</span>
        <i
          className={`ri-arrow-down-s-line text-foreground-500 flex-shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
          style={{ width: 20, height: 20 }}
        />
      </button>
      {isOpen && (
        <div className="px-5 pb-5 text-sm text-foreground-700 leading-relaxed">
          {answer}
        </div>
      )}
    </div>
  );
}

export default function FaqPage() {
  const { t } = useTranslation();

  useEffect(() => {
    document.title = `${t('faqH2')} | CSSBuy Spreadsheet`;
  }, [t]);

  const faqs = [
    { question: t('faq1Q'), answer: t('faq1A') },
    { question: t('faq2Q'), answer: t('faq2A') },
    { question: t('faq3Q'), answer: t('faq3A') },
    { question: t('faq4Q'), answer: t('faq4A') },
    { question: 'Como funciona o warehouse?', answer: 'O warehouse permite que você armazene produtos comprados antes de enviá-los. Isso permite combinar vários produtos em um único envio, reduzindo custos de frete.' },
    { question: 'Posso devolver produtos?', answer: 'As políticas de devolução dependem do vendedor e do agente. Verifique as políticas atuais antes de fazer uma compra. Sempre consulte o guia de safety para proteger suas transações.' },
    { question: 'Quais métodos de pagamento são aceitos?', answer: 'Os métodos de pagamento variam. Verifique as opções disponíveis no site oficial. Sempre use métodos seguros e verifique URLs antes de realizar pagamentos.' },
  ];

  return (
    <div className="min-h-screen bg-background-50">
      <Header />
      <main>
        <div className="max-w-6xl mx-auto px-4 md:px-6 pt-24 pb-4">
          <nav className="flex items-center gap-2 text-sm text-foreground-500">
            <Link to="/" className="hover:text-primary-600 transition-colors">{t('breadcrumbHome')}</Link>
            <i className="ri-arrow-right-s-line" style={{ width: 16, height: 16 }} />
            <span className="text-foreground-700">{t('breadcrumbFAQ')}</span>
          </nav>
        </div>

        <section className="py-8 md:py-12">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <h1 className="font-heading text-3xl md:text-5xl text-foreground-950 mb-6">
              {t('faqH2')}
            </h1>
            <p className="text-foreground-700 mb-10">
              Encontre respostas para as perguntas mais comuns sobre o CSSBuy Spreadsheet, pesquisa de produtos, envio, QC e segurança.
            </p>
            <div className="space-y-3">
              {faqs.map((faq, index) => (
                <FAQItem key={index} question={faq.question} answer={faq.answer} />
              ))}
            </div>

            <div className="mt-10 pt-8 border-t border-background-200/70">
              <h3 className="font-semibold text-foreground-950 mb-4">{t('articleRelated')}</h3>
              <div className="flex flex-wrap gap-3">
                <Link to="/cssbuy-spreadsheet" className="px-4 py-2 bg-background-100 rounded-lg text-sm text-foreground-700 hover:text-primary-600 hover:bg-primary-50 transition-colors">{t('breadcrumbSpreadsheet')}</Link>
                <Link to="/cssbuy-guide" className="px-4 py-2 bg-background-100 rounded-lg text-sm text-foreground-700 hover:text-primary-600 hover:bg-primary-50 transition-colors">{t('navGuide')}</Link>
                <Link to="/cssbuy-safety" className="px-4 py-2 bg-background-100 rounded-lg text-sm text-foreground-700 hover:text-primary-600 hover:bg-primary-50 transition-colors">{t('navSafety')}</Link>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}